import { Box, Container, Grid, Typography, useTheme } from "@mui/material";
import { aboutUsContent, Section, values } from "../data/whoweare";
import WhyWorkAtHungerford from "../components/common/WhyWorkAtHungerford";
import IconListItem from "../components/common/IconListItem";
import HeroParallaxSection from "../components/sections/HeroParallaxSection";
import useResponsiveSpacingGrid from "../hooks/useResponsiveSpacingGrid";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SectionMainTitle from "../components/common/SectionMainTitle";
import SectionCard from "../components/common/SectionCard";
import SectionYellowTitle from "../components/common/SectionYellowTitle";

const WhoWeArePage: React.FC = () => {
  const theme = useTheme();
  const spacing = useResponsiveSpacingGrid();

  return (
    <>
      {/* Hero background image */}
      <HeroParallaxSection
        sectionLabel={aboutUsContent.title}
        subtitle={aboutUsContent.intro}
        showButton={false}
        showTitleResponsive={true}
        backgroundImage={aboutUsContent.image}
      />

      {/* Sections Who We Are */}
      <Container>
        {aboutUsContent.sections.map((section: Section, idx) => {
          if ("content" in section) {
            // Caso: heading + content → WhyWorkAtHungerford
            return (
              <WhyWorkAtHungerford
                key={idx}
                heading={section.heading}
                content={section.content}
                imageSrc={section.image}
                imageAlt={section.imageAlt}
                reverse={idx % 2 === 1}
                maxWidth="2000px"
                alignTextVertical="top"
                mTop={15}
                imageVariant={section.imageVariant ?? "default"}
              />
            );
          }

          if ("items" in section) {
            // Caso: heading + items → IconListItem
            return (
              <Box
                key={idx}
                sx={{
                  mt: { xs: 6, md: 4, lg: 16 },
                  mb: { xs: 2, md: 4, lg: 16 },
                }}
              >
                <SectionYellowTitle title={section.heading} />
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{ fontWeight: 700, mb: { xs: 4, md: 4, lg: 4 }, mt: 2 }}
                >
                  {section.title}
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ mb: { xs: 4, lg: 6 }, textAlign: "justify" }}
                >
                  {section.description}
                </Typography>

                <Grid container spacing={spacing} alignItems="stretch">
                  {section.items.map((item, i) => (
                    <Grid size={{ xs: 12, md: 6 }} key={i}>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.text.disabled}`,
                          padding: 2,
                          borderRadius: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                        }}
                      >
                        <IconListItem
                          key={i}
                          title={item.title}
                          text={item.text}
                          color="white"
                          align="row"
                          icon={
                            <TaskAltIcon
                              sx={{
                                color: theme.palette.primary.main,
                                mt: "3px",
                              }}
                            />
                          }
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          }
          return null;
        })}
      </Container>
      {/* Sections Values */}

      <Container sx={{ mt: { xs: 6, md: 16 } }}>
        {/* Título principal */}
        <SectionMainTitle title={values.heading} mb={5} />

        {/* Cards con Grid responsive */}
        {values.items.map((section, index) => (
          <Grid size={{ xs: 12, md: 4 }} key={index}>
            <SectionCard
              heading={section.title}
              body={section.text}
              icon={section.icon}
              iconColor={section.iconColor}
              iconBg={section.iconBg}
              highlightColor={section.highlightColor}
            />
          </Grid>
        ))}
      </Container>
    </>
  );
};

export default WhoWeArePage;
